/**
 * The planner's filter semantics — ONE definition, shared by both surfaces.
 *
 * WHY THIS FILE EXISTS
 * The calendar grid and the scheduled-messages sidebar are driven by the same
 * `state.CalendarReducer.calendarSidebarFilters`, but each used to interpret it in its
 * own code path. They drifted, and the drift was user-visible: with "Failed only" on,
 * the sidebar listed failed sends while the grid said "Nothing scheduled this month"
 * for the very same month. Both surfaces now call `messageMatchesPlannerFilters` and
 * nothing else decides. Keep it that way.
 *
 * THE MODEL (the product owner's, September 2026)
 * Two questions, asked separately:
 *
 *   WHAT KIND of send?     Scheduled messages · Scheduled campaigns · Statuses
 *   WHICH OUTCOMES?        Show failed · Show sent
 *
 *     visible  =  its kind is on  AND  ( it has not gone out yet  OR  its outcome's toggle is on )
 *
 * Every send is exactly one of three OUTCOMES:
 *   upcoming — not resolved yet (scheduled, waiting, rate-limited, in flight). ALWAYS shown
 *              for an enabled kind: it is work still ahead of the user.
 *   failed   — resolved, and it did not go out. Behind "Show failed" (default ON).
 *   sent     — resolved, and it went out. Behind "Show sent" (default OFF — "sent shows on
 *              the calendar only if the user ticked that they want it").
 * The outcome toggles apply across whichever kinds are on: campaigns on + Show failed on
 * shows the campaign sends that failed, and so on.
 *
 * "Chats: All / Groups only" is a different axis (WHO it is to, not what happened) and is
 * AND-ed on top.
 *
 * THE EXHAUSTIVENESS RULE — the bug this rewrite closes
 * The previous model listed the statuses it knew about in three separate places (the
 * source query, the "failure" set, the "history" set) and let everything else fall
 * through. `error`, `delivered`, `sending`, `bt-sent`, `before-wa-send` and `cancelled`
 * fell through: the predicate called them visible while the source query never selected
 * them, so they were invisible under EVERY filter state, and "Failed" could not match a
 * genuinely errored send. So now:
 *
 *   1. `OUTCOME_OF_STATUS` is a `Record` over the WHOLE effective-status union — a status
 *      added to the enum without a decision here is a COMPILE error, not a silent hole.
 *   2. `RAW_STATUS_OUTCOMES` records, per RAW doc status, every outcome the effective-
 *      status pass can turn it into. The source query is DERIVED from it, never listed by
 *      hand, and a test enumerates every raw status × every doc variant to prove the table
 *      covers what `getEffectiveMessageStatus` actually does. That is what makes "the query
 *      fetches it" and "the predicate can show it" the same statement.
 *
 * WHAT A SENT SEND LOOKS LIKE IN THE DATABASE — the rule "Show sent" stands on
 * The backend takes a delivered send out of the queue by SOFT-deleting it
 * (`deleteUserMessageAndCreateLog`: a real `waMessageKey` and a status other than
 * `sent_pending_ack` → `deleted: true`, status `confirmed`). So every send that genuinely
 * went out is `deleted: true` — and a filter that dropped every deleted row could never
 * show one (the account the diagnosis ran on had 704 sent rows in one month and "Show sent"
 * drew none). A user's cancellation is ALSO `deleted: true`, but it keeps a queue status
 * (pending / failed) and carries no key. `isSentRecord` is the one test that tells them
 * apart; nothing else in the planner may look at `deleted` for this.
 *
 * "SAME CALENDAR, ONLY MARKED SENT" — the product owner's rule for what Show sent draws
 * Before a campaign goes out the calendar draws ONE chip for it; after, the same chip, in
 * the same place, marked. So campaign sends are never drawn one by one on the calendar: a
 * completed campaign (`CAMPAIGN_OUTCOME_OF_STATUS`) stands for all of them, and carries the
 * split of what went out and what did not (`campaignRecipientSplit`).
 */
import { chatKindOf } from '@bt-common/schemas/wa/chat-kind';
import { UserMessage } from '@bt-common/schemas/bt/user-message';
import { MessageStatusList, type MessageStatus } from '@bt-common/schemas/bt/message-status';
import {
    EffectiveMessageStatus,
    getEffectiveMessageStatus,
} from '@common-components/message-status/message-status-config';
import { sendKindOf } from '@common/components/kind-glyphs/send-kind';

// ─── the stored shape ────────────────────────────────────────────────────────

export interface PlannerFilters {
    /** KIND — one-off and recurring messages (and polls) to people and groups. */
    showMessages: boolean;
    /** KIND — campaigns: the campaign chip, plus its individual sends once they resolve. */
    showCampaigns: boolean;
    /** KIND — WhatsApp Status posts (recipient `status@broadcast`, via `sendKindOf`). */
    showStatuses: boolean;
    /** OUTCOME — resolved sends that did NOT go out. */
    showFailed: boolean;
    /** OUTCOME — resolved sends that DID go out. */
    showSent: boolean;
    /** RECIPIENT — only sends to WhatsApp groups. A different axis; AND-ed on top. */
    groupsOnly: boolean;
}

/** What a new planner shows: every kind, failures, and NOT what was already sent. */
export const DEFAULT_PLANNER_FILTERS: PlannerFilters = {
    showMessages: true,
    showCampaigns: true,
    showStatuses: true,
    showFailed: true,
    showSent: false,
    groupsOnly: false,
};

/** Every switch open — the widest the planner can ever be. */
export const PERMISSIVE_PLANNER_FILTERS: PlannerFilters = {
    showMessages: true,
    showCampaigns: true,
    showStatuses: true,
    showFailed: true,
    showSent: true,
    groupsOnly: false,
};

/** The keys, in panel order. Anything enumerating filter states walks THIS list. */
export const PLANNER_FILTER_KEYS: ReadonlyArray<keyof PlannerFilters> = [
    'showMessages', 'showCampaigns', 'showStatuses', 'showFailed', 'showSent', 'groupsOnly',
];

/**
 * Read ANY stored value as the current shape — the one door every reader goes through.
 *
 * The filters live only in redux (they are not persisted: no localStorage, no
 * chrome.storage, no redux-persist — checked), so no user can carry the OLD shape across
 * a reload. This exists because the old shape can still ARRIVE — from a caller or a test
 * written against it, or a partial object — and a missing key must never read as `false`:
 * `showStatuses: undefined` would silently hide every Status.
 *
 * Mapping from the previous shape:
 *   - `showPast` ("Sends that already went out") → `showSent`. Under the old model
 *     failures were ALWAYS visible, so `showFailed` is on whatever `showPast` said —
 *     `showPast: true` therefore lands as both outcome toggles on, `false` as failed only.
 *   - a Status used to count as a message, so an old `showMessages: false` also hid
 *     Statuses; `showStatuses` inherits it so the planner shows exactly what it did.
 *   - `failedOnly` / `rateLimitedOnly` (the removed status chips) are dropped: the
 *     outcome toggles now answer that question, and an old "only failed" narrowing has
 *     no faithful equivalent that would not also hide the user's upcoming work.
 */
export function normalizePlannerFilters(raw: unknown): PlannerFilters {
    if (!raw || typeof raw !== 'object') return { ...DEFAULT_PLANNER_FILTERS };
    const r = raw as Record<string, unknown>;
    const bool = (v: unknown): boolean | undefined => (typeof v === 'boolean' ? v : undefined);

    const showMessages = bool(r.showMessages) ?? DEFAULT_PLANNER_FILTERS.showMessages;
    return {
        showMessages,
        showCampaigns: bool(r.showCampaigns) ?? DEFAULT_PLANNER_FILTERS.showCampaigns,
        showStatuses: bool(r.showStatuses) ?? (bool(r.showMessages) ?? DEFAULT_PLANNER_FILTERS.showStatuses),
        showFailed: bool(r.showFailed) ?? DEFAULT_PLANNER_FILTERS.showFailed,
        showSent: bool(r.showSent) ?? bool(r.showPast) ?? DEFAULT_PLANNER_FILTERS.showSent,
        groupsOnly: bool(r.groupsOnly) ?? DEFAULT_PLANNER_FILTERS.groupsOnly,
    };
}

// ─── outcome: every status, exactly one answer ────────────────────────────────

export type PlannerOutcome = 'upcoming' | 'sent' | 'failed';
/** Deliberately not a planner row at all — see `cancelled` below. */
export type PlannerExclusion = 'excluded';

/**
 * EVERY effective status → exactly one outcome. A `Record`, so a status added to the enum
 * without a decision here does not compile.
 *
 * The in-between statuses, decided deliberately:
 *   - `before-wa-send`, `bt-sent`, `sending` → UPCOMING. The send is in flight: nothing has
 *     reached WhatsApp yet and nothing is resolved. A doc STUCK in one of these is exactly the
 *     send a user needs to see, so it must never wait behind a toggle that is off by default.
 *   - `unconfirmed` (handed to WhatsApp, no delivery ack — raw `sent`/`sent_pending_ack`)
 *     → SENT. It left Blueticks with a real WhatsApp key; manual re-send is blocked on it
 *     because a retry would duplicate (`canManuallySend`); for a group it is routine and can
 *     last weeks. Calling it upcoming would put "sent, not confirmed" chips on every default
 *     calendar; calling it failed would be a claim we cannot back. Its chip still says
 *     "not confirmed" — this only decides which toggle reveals it.
 *   - `cancelled` → EXCLUDED. The user cancelled it; per the status vocabulary the userMessage
 *     is deleted and the status lives in the log. It is not a send that happened, failed or is
 *     coming, so no toggle can honestly reveal it — the same treatment as `deleted: true`.
 *   - `error` → FAILED. It is a send that raised an error; it did not go out.
 *   - `delivered` → SENT (the log-vocabulary twin of `received`).
 */
export const OUTCOME_OF_STATUS: Readonly<Record<EffectiveMessageStatus, PlannerOutcome | PlannerExclusion>> = {
    // not resolved yet
    pending: 'upcoming',
    rate_limited: 'upcoming',
    // #480: still-`pending` doc with a real waMessageKey — unresolved until the ack lands, so it
    // stays with the upcoming bucket the raw `pending` doc was already in (counts do not move).
    in_transit: 'upcoming',
    'before-wa-send': 'upcoming',
    'bt-sent': 'upcoming',
    sending: 'upcoming',
    // resolved — went out
    sent: 'sent',
    sent_pending_ack: 'sent',
    unconfirmed: 'sent',
    confirmed: 'sent',
    delivered: 'sent',
    received: 'sent',
    read: 'sent',
    played: 'sent',
    // resolved — did not go out
    failed: 'failed',
    error: 'failed',
    expired: 'failed',
    // not a send
    cancelled: 'excluded',
};

/**
 * Every outcome a RAW doc status can become once `getEffectiveMessageStatus` has run.
 * The effective pass moves docs ACROSS outcomes, which is why the query cannot simply use
 * `OUTCOME_OF_STATUS` on the raw value:
 *   - `pending`  → upcoming, or rate_limited (upcoming), or expired (failed) when overdue and
 *                  never dispatched, or unconfirmed/confirmed (sent) when overdue but dispatched;
 *   - `expired`  → failed, or unconfirmed/confirmed (sent) when it carries send evidence (#141);
 *   - `failed`   → failed, or confirmed (sent) when it carries a delivery ack (FE#249);
 *   - `sent`/`sent_pending_ack` → unconfirmed or confirmed — sent either way.
 * Pinned against the real `getEffectiveMessageStatus` by an exhaustive variant test.
 */
export const RAW_STATUS_OUTCOMES: Readonly<Record<MessageStatus, ReadonlyArray<PlannerOutcome>>> = {
    pending: ['upcoming', 'failed', 'sent'],
    'before-wa-send': ['upcoming'],
    'bt-sent': ['upcoming'],
    sending: ['upcoming'],
    sent: ['sent'],
    sent_pending_ack: ['sent'],
    confirmed: ['sent'],
    delivered: ['sent'],
    received: ['sent'],
    read: ['sent'],
    played: ['sent'],
    cancelled: [],
    error: ['failed'],
    failed: ['failed', 'sent'],
    expired: ['failed', 'sent'],
};

/**
 * The statuses a send has once WhatsApp accepted it — the only statuses the backend ever
 * soft-deletes a row with (a key + anything but `sent_pending_ack`; an ack later promotes
 * `sent_pending_ack` to `confirmed`, which then deletes it). Also the statuses the lazy
 * sent-history load asks for.
 */
export const SENT_RECORD_STATUSES: ReadonlyArray<MessageStatus> = ['confirmed', 'delivered', 'received', 'read', 'played'];

/** A WhatsApp key that identifies a real message: a Baileys string, or a wa-js key object
 *  with at least one field set. The all-null shell every new doc is created with is NOT one
 *  (see `hasRealWaMessageKey` in message-status-config, the same rule). */
function hasRealKey(key: unknown): boolean {
    if (key == null) return false;
    if (typeof key === 'string') return key.length > 0;
    if (typeof key !== 'object') return true;
    return Object.values(key as Record<string, unknown>).some(v => v !== null && v !== undefined && v !== '');
}

/**
 * Is this deleted row the record of a send that went out? `deleted: true` AND a delivered
 * status AND a real WhatsApp key. A user's cancellation is deleted too, but keeps its
 * queue status and has no key; a `sent_pending_ack` the public API soft-deleted has a key
 * but was never confirmed — neither is a sent record.
 *
 * Nothing in the app re-deletes a row that is already a sent record: the app's own delete
 * is a hard `DELETE` (the row is gone, it cannot come back), and the API's delete only
 * reaches rows that are not yet deleted.
 */
export function isSentRecord(message: { deleted?: unknown; status?: unknown; waMessageKey?: unknown } | null | undefined): boolean {
    return !!message
        && message.deleted === true
        && SENT_RECORD_STATUSES.includes(message.status as MessageStatus)
        && hasRealKey(message.waMessageKey);
}

/** Outcome of an effective status; `undefined` for a doc with no status at all. */
export function outcomeOfStatus(status: EffectiveMessageStatus | undefined): PlannerOutcome | PlannerExclusion | undefined {
    if (status === undefined) return undefined;
    return OUTCOME_OF_STATUS[status] ?? 'excluded';
}

/** Outcome of a message (computes its effective status unless the caller already has it). */
export function plannerOutcomeOf(
    message: UserMessage,
    effectiveStatus?: EffectiveMessageStatus,
): PlannerOutcome | PlannerExclusion | undefined {
    return outcomeOfStatus(effectiveStatus ?? getEffectiveMessageStatus(message));
}

// ─── kind ───────────────────────────────────────────────────────────────────

export type PlannerKind = 'messages' | 'campaigns' | 'statuses';

/**
 * Which KIND toggle governs this message. A campaign's send (`campaignId`) is a campaign
 * whatever its recipient. Otherwise `sendKindOf` decides Status vs message — the same test
 * the chip and the sidebar row use, so a send cannot be a Status on one surface and a
 * message on another. A poll is a message.
 */
export function plannerKindOf(message: Pick<UserMessage, 'contactList'> & { campaignId?: unknown; isPoll?: boolean | null }): PlannerKind {
    if (message?.campaignId) return 'campaigns';
    return sendKindOf({ isPoll: message?.isPoll, contactList: message?.contactList as any }) === 'status'
        ? 'statuses'
        : 'messages';
}

export function isKindShown(filters: PlannerFilters, kind: PlannerKind): boolean {
    return kind === 'campaigns' ? filters.showCampaigns
        : kind === 'statuses' ? filters.showStatuses
            : filters.showMessages;
}

function isOutcomeShown(filters: PlannerFilters, outcome: PlannerOutcome): boolean {
    return outcome === 'upcoming' ? true
        : outcome === 'failed' ? filters.showFailed
            : filters.showSent;
}

// ─── campaigns ──────────────────────────────────────────────────────────────

/** Every campaign status the app knows, including the legacy spellings still persisted. */
export type PlannerCampaignStatus =
    | 'initial' | 'pending' | 'active' | 'running' | 'paused'
    | 'completed' | 'complete-send' | 'complete-sent' | 'complete-delivered' | 'complete-with-skips'
    | 'aborted';

/**
 * EVERY campaign status → one outcome, the campaign twin of `OUTCOME_OF_STATUS` (a `Record`,
 * so a new status is a compile error until someone decides it).
 *   - queued, running or held → UPCOMING: the chip the calendar already draws. A RUNNING
 *     campaign is in flight, exactly like a message that is `sending` — it stays on the
 *     calendar until it resolves, so a step that finishes while the page is open goes from
 *     upcoming to sent without an empty moment in between.
 *   - every `complete*` spelling → SENT: the same chip, marked (how much of it went out is
 *     `campaignRecipientSplit`'s job, not the status's — `complete-with-skips` is a sent
 *     campaign that did not reach everyone).
 *   - `aborted` → EXCLUDED: the user stopped it, the campaign twin of a cancelled message.
 */
export const CAMPAIGN_OUTCOME_OF_STATUS: Readonly<Record<PlannerCampaignStatus, 'upcoming' | 'sent' | PlannerExclusion>> = {
    initial: 'upcoming',
    pending: 'upcoming',
    active: 'upcoming',
    running: 'upcoming',
    paused: 'upcoming',
    completed: 'sent',
    'complete-send': 'sent',
    'complete-sent': 'sent',
    'complete-delivered': 'sent',
    'complete-with-skips': 'sent',
    aborted: 'excluded',
};

export function campaignOutcomeOf(status: string | undefined | null): 'upcoming' | 'sent' | PlannerExclusion {
    if (!status) return 'upcoming';
    return CAMPAIGN_OUTCOME_OF_STATUS[status as PlannerCampaignStatus] ?? 'excluded';
}

/** The statuses the lazy completed-campaign load asks for — derived, never listed twice. */
export const SENT_CAMPAIGN_STATUSES = (Object.keys(CAMPAIGN_OUTCOME_OF_STATUS) as PlannerCampaignStatus[])
    .filter(s => CAMPAIGN_OUTCOME_OF_STATUS[s] === 'sent');

/**
 * How many of a campaign's recipients a message went out to, and how many it did not —
 * straight from the campaign document plus the failed sends already in the store, so it
 * costs no per-recipient fetch.
 *
 *   queued   = `contactCount` — the recipients the backend actually queued (after dropping
 *              duplicates and people who left a sequence, which are deliberate, not misses)
 *   skipped  = `skippedCount` — recipients with no WhatsApp id: never sent to
 *   failed   = this campaign's failed sends (they stay live `failed` rows, loaded eagerly)
 *   total    = queued + skipped;  notSent = failed + skipped;  sent = queued − failed
 *
 * `null` when the document does not say how many it queued: then the chip shows only what
 * is known (the failures), never a made-up denominator.
 */
export interface CampaignRecipientSplit { total: number; sent: number; notSent: number; failed: number; skipped: number }

export function campaignRecipientSplit(
    campaign: { contactCount?: unknown; skippedCount?: unknown } | null | undefined,
    failedCount: number,
): CampaignRecipientSplit | null {
    const queued = typeof campaign?.contactCount === 'number' ? campaign.contactCount : undefined;
    if (queued === undefined) return null;
    const skipped = typeof campaign?.skippedCount === 'number' ? Math.max(0, campaign.skippedCount) : 0;
    const failed = Math.max(0, Math.min(failedCount, queued));
    return { total: queued + skipped, sent: queued - failed, notSent: failed + skipped, failed, skipped };
}

/**
 * Is a campaign CHIP drawn? The campaign twin of the message predicate: its kind must be
 * on (and it is never "a group"), an upcoming chip always shows, and a sent chip shows when
 * what it stands for is asked for — anything that went out under "Show sent", anything that
 * did not under "Show failed". A campaign that reached some and missed some answers to both.
 */
export function campaignChipVisible(
    filters: PlannerFilters,
    outcome: 'upcoming' | 'sent' | PlannerExclusion,
    split: Pick<CampaignRecipientSplit, 'sent' | 'notSent'> | null,
): boolean {
    const f = normalizePlannerFilters(filters);
    if (!campaignsVisible(f) || outcome === 'excluded') return false;
    if (outcome === 'upcoming') return true;
    const wentOut = split ? split.sent > 0 : true;
    const missed = split ? split.notSent > 0 : false;
    return (f.showSent && wentOut) || (f.showFailed && missed);
}

// ─── the source query ─────────────────────────────────────────────────────────

/**
 * The raw doc statuses a surface should SELECT for a filter state — derived from
 * `RAW_STATUS_OUTCOMES`, never listed by hand. A raw status is selected exactly when one of
 * the outcomes it can become is currently shown. Upcoming is always shown, so everything in
 * flight is always selected; `cancelled` can become nothing, so it is never selected.
 */
export function plannerSourceStatuses(filters: PlannerFilters): MessageStatus[] {
    const f = normalizePlannerFilters(filters);
    return MessageStatusList.filter(raw => RAW_STATUS_OUTCOMES[raw].some(o => isOutcomeShown(f, o)));
}

/**
 * What gets FETCHED from the server into the store, and how. The store query above can only
 * select what one of these loads brought in, so together they must cover every raw status the
 * planner can show — pinned by a test (a status nobody loads is the `error` bug again, one
 * layer down). Derived from `RAW_STATUS_OUTCOMES` wherever the rule is mechanical:
 *
 *   eagerUnbounded — everything that can be UPCOMING, plus `failed`: outstanding work, small,
 *                    needed whatever the toggles say ("Clear failed" and the counts read it).
 *   eagerBounded   — terminal failures that accumulate on a heavy account (`expired` from the
 *                    max-send-window reaper, `error`): newest-first with a logged cap.
 *   unconfirmedTail — `sent` / `sent_pending_ack`, paginated to the end (#166/#169).
 *   deliveredRange — sent history, loaded ONLY while "Show sent" is on and only for the
 *                    visible range (planner-sent-history.ts). Soft-deleted rows included —
 *                    that is what a sent send is — and campaign sends excluded at the server:
 *                    their completed campaign stands for them.
 */
const BOUNDED_TERMINAL: ReadonlyArray<MessageStatus> = ['expired', 'error'];
const UNCONFIRMED_TAIL: ReadonlyArray<MessageStatus> = ['sent', 'sent_pending_ack'];

export const PLANNER_LOAD_PLAN = {
    eagerUnbounded: MessageStatusList.filter(raw =>
        RAW_STATUS_OUTCOMES[raw].some(o => o === 'upcoming' || o === 'failed') && !BOUNDED_TERMINAL.includes(raw)),
    eagerBounded: [...BOUNDED_TERMINAL],
    unconfirmedTail: [...UNCONFIRMED_TAIL],
    deliveredRange: MessageStatusList.filter(raw =>
        RAW_STATUS_OUTCOMES[raw].length > 0
        && RAW_STATUS_OUTCOMES[raw].every(o => o === 'sent')
        && !UNCONFIRMED_TAIL.includes(raw)),
} as const;

// ─── the predicate ────────────────────────────────────────────────────────────

/**
 * Recipient is a WhatsApp group (`@g.us`).
 *
 * Falls back to the persisted `waMessageKey` remote/serialized form exactly as
 * `isGroupSend` in message-status-config does. Without the fallback the two disagreed on a
 * row whose `contactList` was never hydrated: the status logic treated it as a group while
 * "Groups only" hid it. One recipient, two answers, is the class of drift this module
 * exists to end.
 */
export function isGroupMessage(message: Pick<UserMessage, 'contactList'> & { waMessageKey?: unknown }): boolean {
    if (chatKindOf(message?.contactList?.[0]?.id) === 'group') return true;

    const key = message?.waMessageKey as
        | { remote?: unknown; remoteJid?: unknown; _serialized?: unknown }
        | undefined;
    if (!key || typeof key !== 'object') return false;
    if (typeof key.remoteJid === 'string' && chatKindOf(key.remoteJid) === 'group') return true;
    if (typeof key.remote === 'string' && chatKindOf(key.remote) === 'group') return true;
    // `_serialized` is `fromMe_chatId_msgId[_participant]`; the chat JID is the middle
    // segment(s) — a group JID contains '-' but no '@' until its suffix.
    if (typeof key._serialized === 'string' && key._serialized.includes('@g.us')) {
        const parts = key._serialized.split('_');
        if (parts.length >= 3 && chatKindOf(parts.slice(1, -1).join('_')) === 'group') return true;
    }
    return false;
}

/**
 * Are campaign CHIPS (the campaign documents themselves) shown? A campaign chip is an
 * upcoming campaign, so only its kind gates it — plus "Groups only", which it cannot
 * satisfy (it is not a chat), exactly as before.
 */
export function campaignsVisible(filters: PlannerFilters): boolean {
    const f = normalizePlannerFilters(filters);
    return f.showCampaigns && !f.groupsOnly;
}

/**
 * THE predicate. Both surfaces render a message if and only if this returns true.
 *
 * `effectiveStatus` may be passed in when the caller already computed it (the sidebar
 * builds a status map for its "Clear failed" affordance and reuses it here).
 */
export function messageMatchesPlannerFilters(
    message: UserMessage,
    filters: PlannerFilters,
    effectiveStatus?: EffectiveMessageStatus,
): boolean {
    // The only deleted row the planner may show is the record of a send that went out.
    if (!message || (message.deleted === true && !isSentRecord(message))) return false;
    const f = normalizePlannerFilters(filters);

    const outcome = plannerOutcomeOf(message, effectiveStatus);
    // No status, or a status that is not a send (cancelled): no toggle can reveal it, and
    // the source query never selects it — the two agree by construction.
    if (outcome === undefined || outcome === 'excluded') return false;

    const kind = plannerKindOf(message as any);
    if (!isKindShown(f, kind)) return false;

    // A campaign's UPCOMING sends are represented by the campaign chip — drawing each one
    // would bury the schedule under one campaign's hundreds of sends. A RESOLVED one passes
    // here, so the sidebar LIST can name it; the CALENDAR never draws it on its own — it is
    // folded into its campaign's chip ("same calendar, only marked"), see
    // getCalendarCampaignSendFolds in WhatsAppSelectors.
    if (kind === 'campaigns' && outcome === 'upcoming') return false;

    if (f.groupsOnly && !isGroupMessage(message)) return false;

    return isOutcomeShown(f, outcome);
}

/**
 * Could this message be shown by the planner under ANY filter state? The honest
 * denominator for "N of M sends match your filters": a genuine superset of
 * `messageMatchesPlannerFilters` for every possible `PlannerFilters` value, so the ratio
 * can never exceed 1. (Pinned by a test that enumerates all 2^6 filter states.)
 */
export function isPlannerCandidate(message: UserMessage): boolean {
    return messageMatchesPlannerFilters(message, PERMISSIVE_PLANNER_FILTERS);
}

// ─── what the panel, the badge and the empty states say ─────────────────────────

/**
 * Is the planner hiding something the DEFAULT would show? Any kind off, failures off, or
 * "Groups only". Turning "Show sent" on shows MORE than the default, so it never narrows.
 * This is what licenses an empty state to say "your filters are hiding it".
 */
export function isNarrowed(filters: PlannerFilters): boolean {
    const f = normalizePlannerFilters(filters);
    return !f.showMessages || !f.showCampaigns || !f.showStatuses || !f.showFailed || f.groupsOnly;
}

/**
 * Why is the planner LIST empty? Lets an empty state explain itself instead of saying
 * "nothing scheduled" when the truth is "your filters hid it".
 *   - `nothing-at-all` — there is nothing, full stop (checked FIRST: a brand-new user
 *     with a toggle set must not be told their filters are hiding something);
 *   - `search`         — a search is what emptied it;
 *   - `sent-hidden`    — nothing narrows the view and "Show sent" is off, yet there are
 *     sends: what is left is history, which only "Show sent" reveals. Said WITHOUT a count —
 *     counting would mean fetching sent rows the user did not ask to see;
 *   - `filtered`       — a narrowing filter is hiding things.
 */
export type PlannerEmptyReason = 'nothing-at-all' | 'search' | 'sent-hidden' | 'filtered';

export function plannerEmptyReason(
    filters: PlannerFilters,
    totalBeforeFilters: number,
    hasSearchToken: boolean,
): PlannerEmptyReason {
    if (totalBeforeFilters === 0) return 'nothing-at-all';
    if (hasSearchToken) return 'search';
    if (!isNarrowed(filters) && !normalizePlannerFilters(filters).showSent) return 'sent-hidden';
    return 'filtered';
}

/**
 * Some toggle combinations cannot match anything, ever. Naming the conflict is the honest
 * alternative to silently returning nothing.
 *  - `nothing-selected`: every KIND is off, so the surface is empty by construction.
 * The outcome toggles cannot produce a conflict: upcoming sends are always shown.
 */
export type PlannerFilterConflict = 'nothing-selected' | null;

export function plannerFilterConflict(filters: PlannerFilters): PlannerFilterConflict {
    const f = normalizePlannerFilters(filters);
    if (!f.showMessages && !f.showCampaigns && !f.showStatuses) return 'nothing-selected';
    return null;
}
